import React, { useEffect, useRef } from "react";
import AddressSession from "../session/AddressSession";

import "../style/Session.css";
import { useDispatch, useSelector } from "react-redux";
import { setChosenLocationEmpty, setNavBarMode } from "../api/xoicom.reducer";
import {
  IMAGES,
  getLanguageVersionFromStorage,
  checkOthers,
} from "../utils/utils";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import IngredientBlog from "../components/ingredient/IngredientBlog";
import IngredientBlogMobile from "../components/ingredient/IngredientBlogMobile";

import { fetchIngredientDetails } from "../api/ingredient/ingredient.reduxThunk";
import { fetchByCategoryAndId, INGREDIENT } from "../api/utils.api";
import { useMediaQuery } from "@mui/material";
import { breakpoint } from "../utils/responsiveUtils";
import LoadingBackdrop from "../components/skeleton/LoadingBackdrop";
import { slideUp } from "../utils/animationUtils";
import XoiComHeading from "../components/generics/XoiComHeading";

const USE_INGREDIENT_SLUG =
  process.env.REACT_APP_USE_INGREDIENT_SLUG === "TRUE";

function IngredientDetail() {
  const params = useParams();
  const slugOrId = USE_INGREDIENT_SLUG ? params.slug : params.id;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const language = useSelector((state) => state.reducer.language);
  const ingredient = useSelector((state) => state.reducer.ingredientDetails);
  const ingredientText = useSelector((state) => state.reducer.ingredientText);
  const ingredientPreview = useSelector(
    (state) => state.reducer.ingredientPreview,
  );
  const loading = useSelector((state) => state.reducer.loading);
  const mobileSm = useMediaQuery(breakpoint.mobileSm);

  // Slug path: fetch on slug change (initial load + language-switch navigation)
  useEffect(() => {
    if (!USE_INGREDIENT_SLUG) return;
    dispatch(fetchIngredientDetails(slugOrId))
      .unwrap()
      .catch(() => navigate("/ingredient", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugOrId]);

  // ID path: fetch on language change; Slug path: trigger language switch navigation
  useEffect(() => {
    if (USE_INGREDIENT_SLUG) {
      changeLanguageBySlug();
    } else {
      getIngredientDetailsById();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const ref = useRef(null);
  useEffect(() => {
    dispatch(setNavBarMode("light"));
    setTimeout(() => {
      ref?.current?.scrollIntoView({ behavior: "smooth" });
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getIngredientDetailsById = () => {
    const multiLanguageId =
      getLanguageVersionFromStorage("INGREDIENT", Number(slugOrId), language) ||
      slugOrId;
    dispatch(fetchIngredientDetails(multiLanguageId))
      .unwrap()
      .then(() => navigate(`/ingredient/${multiLanguageId}`));
  };

  const changeLanguageBySlug = async () => {
    if (!ingredient?.languageIds) return;
    if (ingredient.slug !== slugOrId) return; // stale Redux state, wait for slug effect
    const counterpartId = ingredient.languageIds[language];
    if (!counterpartId) return;
    const counterpart = await fetchByCategoryAndId(INGREDIENT, counterpartId);
    if (!counterpart?.slug) return;
    navigate(`/ingredient/${counterpart.slug}`);
  };

  const handleBack = (fullReset) => {
    const othersExit = USE_INGREDIENT_SLUG
      ? ingredientPreview.length > 1
      : checkOthers("INGREDIENT", ingredient.id);
    if (fullReset || !othersExit) {
      dispatch(setChosenLocationEmpty());
    }
    navigate("/ingredient");
  };

  return (
    <div>
      <LoadingBackdrop open={loading} />
      <div className="landing-session">
        <img
          className="landing-image"
          src={IMAGES.INGREDIENT_LANDING}
          alt="ingredient-landing"
        />
        <motion.div
          className="landing-text"
          style={{ maxWidth: 700 }}
          variants={slideUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          <XoiComHeading
            heading={ingredientText.landingText}
            textColor="white"
            bigHeader
          />
        </motion.div>
      </div>
      <div ref={ref}>
        {ingredient && !mobileSm && (
          <IngredientBlog
            ingredient={ingredient}
            handleBack={handleBack}
            ingredientText={ingredientText}
          />
        )}
        {ingredient && mobileSm && (
          <IngredientBlogMobile
            ingredient={ingredient}
            handleBack={handleBack}
            ingredientText={ingredientText}
          />
        )}
      </div>
      <AddressSession mode="cta" />
    </div>
  );
}

export default IngredientDetail;
